import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Persist settings to localStorage so repeat visits (and post-deploy reloads)
// show the studio's real content immediately instead of flashing the hardcoded
// fallbacks in Home/Services/Contact for a beat. We refresh in the background
// every load so updates from the admin propagate within seconds.
const CACHE_KEY = "allure.site.cache.v1";

function readPersistedCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writePersistedCache(next) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    /* storage full or disabled — skip */
  }
}

let cache = readPersistedCache();
const subs = new Set();

export function useSiteSettings() {
  const [settings, setSettings] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    const sub = (next) => setSettings(next);
    subs.add(sub);
    // Always refresh from the server (even if we have a cached copy) so admin
    // edits show up on the next page load without a hard refresh.
    api
      .get("/site")
      .then((r) => {
        cache = r.data;
        writePersistedCache(cache);
        subs.forEach((s) => s(cache));
      })
      .finally(() => setLoading(false));
    return () => subs.delete(sub);
  }, []);

  return { settings, loading };
}

export function updateCachedSettings(next) {
  cache = next;
  writePersistedCache(cache);
  subs.forEach((s) => s(cache));
}
